import React, { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModuleSheet } from '../../dashboard/content/useModuleSheet';
import { useMediaQuery } from 'react-responsive';
import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    Check,
    CheckCheck,
    ChevronLeft,
    Clock,
    FileAudio2,
    Image as ImageIcon,
    MoreHorizontal,
    MessageSquarePlus,
    MessageCircle,
    Mic,
    Paperclip,
    PauseCircle,
    Square,
    X,
    UserRound,
    Video, ChevronLeftIcon, Search,
} from 'lucide-react';
import { WebSocketManager } from '../../WebSocketConfig';
import { useAppContext } from '../../contexts/AppContext';
import { useParamsContext } from '../../contexts/ParamsContext';
import WhatsappInboxService, {
    WhatsappClientOverview,
    WhatsappConversation,
    WhatsappManualTemplate,
    WhatsappMessage,
    WhatsappStartConversationPayload,
} from './Service';
import { Modal } from '../../components/Modal';
import PhoneNumber from '../../components/inputs/PhoneNumber';
import { UserService } from '../configuracoes/usuario/Service';
import Dropdown from '../../components/Dropdown';
import { SettingsRow, SettingsSection } from '../configuracoes/estabelecimento/Form/SettingsGroup';
import { formatPhone } from '../clientes/helpers';
import './Whatsapp.css';

const service = new WhatsappInboxService();
const userService = new UserService();
const MAX_MESSAGE_LENGTH = 500;
const WHATSAPP_AUDIO_MAX_BYTES = 16 * 1024 * 1024;
const WHATSAPP_AUDIO_ACCEPT = '.aac,.amr,.mp3,.m4a,.mp4,.ogg,audio/aac,audio/amr,audio/mpeg,audio/mp4,audio/ogg';
const WHATSAPP_AUDIO_MIME_TYPES = new Set([
    'audio/aac',
    'audio/amr',
    'audio/mpeg',
    'audio/mp4',
    'audio/ogg',
]);
const WHATSAPP_RECORDING_FORMATS = [
    {
        recorderMimeType: 'audio/ogg;codecs=opus',
        fileMimeType: 'audio/ogg',
        extension: 'ogg',
    },
    {
        recorderMimeType: 'audio/ogg; codecs=opus',
        fileMimeType: 'audio/ogg',
        extension: 'ogg',
    },
    {
        recorderMimeType: 'audio/mp4;codecs=mp4a.40.2',
        fileMimeType: 'audio/mp4',
        extension: 'm4a',
    },
    {
        recorderMimeType: 'audio/mp4',
        fileMimeType: 'audio/mp4',
        extension: 'm4a',
    },
    {
        recorderMimeType: 'audio/aac',
        fileMimeType: 'audio/aac',
        extension: 'aac',
    },
] as const;

type WhatsappRecordingFormat = (typeof WHATSAPP_RECORDING_FORMATS)[number];

type ClientOption = {
    uid: string;
    displayName?: string;
    email?: string;
    phoneNumber?: string;
    photoURL?: string;
};

function isSupportedAudioFile(file: File) {
    const extension = file.name.split('.').pop()?.toLowerCase();
    const mimeType = normalizeAudioMimeTypeForFrontend(file.type);

    if (WHATSAPP_AUDIO_MIME_TYPES.has(mimeType)) {
        return true;
    }

    return ['aac', 'amr', 'mp3', 'm4a', 'mp4', 'ogg'].includes(extension || '');
}

function normalizeAudioMimeTypeForFrontend(value?: string) {
    const rawType = String(value || '').trim().toLowerCase();
    const compactType = rawType.replace(/\s+/g, '');

    if (compactType === 'audio/ogg;codecs=opus') {
        return 'audio/ogg';
    }

    if (compactType.startsWith('audio/mp4;')) {
        return 'audio/mp4';
    }

    return rawType;
}

function audioFileTypeForPayload(file: File) {
    const normalizedType = normalizeAudioMimeTypeForFrontend(file.type);
    return normalizedType || file.type;
}

function getSupportedRecordingFormat(): WhatsappRecordingFormat | null {
    if (
        typeof window === 'undefined'
        || typeof navigator === 'undefined'
        || !navigator.mediaDevices?.getUserMedia
        || !('MediaRecorder' in window)
        || typeof window.MediaRecorder.isTypeSupported !== 'function'
    ) {
        return null;
    }

    return WHATSAPP_RECORDING_FORMATS.find((format) =>
        window.MediaRecorder.isTypeSupported(format.recorderMimeType),
    ) || null;
}

function fileToDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

function formatFileSize(size: number) {
    if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function formatDuration(totalSeconds: number) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const paddedSeconds = seconds < 10 ? `0${seconds}` : String(seconds);
    return `${minutes}:${paddedSeconds}`;
}

function upsertConversation(
    items: WhatsappConversation[],
    next: WhatsappConversation,
) {
    const withoutCurrent = items.filter((item) => item._id !== next._id);
    return [next, ...withoutCurrent].sort((a, b) => {
        const aTime = new Date(a.lastMessageAt || a.lastOutboundAt || a.lastInboundAt || 0).getTime();
        const bTime = new Date(b.lastMessageAt || b.lastOutboundAt || b.lastInboundAt || 0).getTime();
        return bTime - aTime;
    });
}

function formatDateTime(value?: string) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

function formatConversationTimestamp(value?: string) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const daysAgo = Math.round((today - day) / dayMs);

    if (daysAgo === 0) {
        return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date);
    }

    if (daysAgo === 1) {
        return 'Ontem';
    }

    if (daysAgo > 1 && daysAgo < 7) {
        const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' }).format(date).replace('.', '');
        return weekday.charAt(0).toUpperCase() + weekday.slice(1);
    }

    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date);
}

function getLocalDateKey(value?: string) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function formatMessageDateLabel(value?: string) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const todayKey = getLocalDateKey(now.toISOString());
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const dateKey = getLocalDateKey(value);

    if (dateKey === todayKey) {
        return 'Hoje';
    }

    if (dateKey === getLocalDateKey(yesterday.toISOString())) {
        return 'Ontem';
    }

    const label = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}),
    }).format(date);

    return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatTime(value?: string) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

function formatDate(value?: string) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    }).format(date);
}

function formatCurrency(value?: number | null) {
    return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
}

function renderKnownTemplateBody(templateName?: string, parameters?: any[]) {
    const values = Array.isArray(parameters)
        ? parameters.map((value) => String(value || '').trim())
        : [];
    const [first, second, third, fourth] = values;

    switch (String(templateName || '')) {
        case 'message_text':
        case 'message_media':
            return second || first || '';
        case 'schedule_reminder_default':
            return `Olá ${first || 'Cliente'}, está na hora de agendar seu próximo atendimento.`;
        case 'offer_schedule':
            return [
                'Temos um horário disponível para você.',
                first ? `Data: ${first}` : '',
                second ? `Horário: ${second}` : '',
            ].filter(Boolean).join('\n');
        case 'offer_schedule_with_discount':
            return [
                'Temos um horário disponível para você.',
                first ? `Data: ${first}` : '',
                second ? `Horário: ${second}` : '',
                third || '',
            ].filter(Boolean).join('\n');
        case 'confirm_schedule':
            return [
                `${first || 'Cliente'}, confirme seu horário.`,
                second ? `Serviço: ${second}` : '',
                third ? `Data/Hora: ${third}` : '',
                fourth ? `Observação: ${fourth}` : '',
            ].filter(Boolean).join('\n');
        case 'confirm_whatsapp_account':
            return [
                `Olá ${first || 'Cliente'}, confirme seu WhatsApp.`,
                second || '',
            ].filter(Boolean).join('\n');
        default:
            return '';
    }
}

function resolveTemplateBody(message: WhatsappMessage, presentation: any) {
    return presentation.bodyText
        || renderKnownTemplateBody(presentation.templateName, presentation.bodyParameters)
        || message.body
        || presentation.templateName
        || 'Template';
}

function conversationTitle(conversation?: WhatsappConversation | null) {
    if (!conversation) return '';
    return conversation.clientName || conversation.profileName || conversation.clientPhoneNumber;
}

function initials(name: string) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '?';
    const second = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
    return `${first}${second}`.toUpperCase();
}

function isPaused(conversation?: WhatsappConversation | null) {
    if (!conversation?.botPausedUntil) return false;
    const until = new Date(conversation.botPausedUntil);
    return !Number.isNaN(until.getTime()) && until > new Date();
}

function messageStatusTitle(message: WhatsappMessage) {
    switch (message.status) {
        case 'read':
            return 'Visualizada';
        case 'delivered':
            return 'Entregue';
        case 'sent':
            return 'Enviada';
        case 'failed':
            return 'Falhou';
        default:
            return '';
    }
}

function appointmentStatusLabel(status?: string) {
    const normalizedStatus = String(status || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    switch (normalizedStatus) {
        case 'pendente':
            return 'Pendente';
        case 'agendado':
            return 'Agendado';
        case 'cancelado':
            return 'Cancelado';
        case 'concluido':
            return 'Concluído';
        default:
            return status || 'Agendamento';
    }
}

function appointmentStatusTone(status?: string) {
    const normalizedStatus = String(status || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    switch (normalizedStatus) {
        case 'pendente':
            return 'orange';
        case 'cancelado':
            return 'gray';
        case 'concluido':
            return 'green';
        default:
            return 'blue';
    }
}

function subscriptionStatusLabel(status?: number) {
    return Number(status) === 1 ? 'Ativa' : 'Cancelada';
}

function ContactAvatar({ name, large = false }: { name: string; large?: boolean }) {
    // Contato sem nome tem so o telefone como titulo: iniciais de digitos nao dizem nada.
    const hasName = !/^[+\d\s()-]+$/.test(name);

    return (
        <span className={`wa-avatar ${large ? 'is-large' : ''}`} aria-hidden>
            {hasName ? initials(name) : <UserRound className={large ? 'h-8 w-8' : 'h-5 w-5'} />}
        </span>
    );
}

function MessageStatus({ message }: { message: WhatsappMessage }) {
    if (message.direction !== 'OUTBOUND') {
        return null;
    }

    const title = messageStatusTitle(message);

    if (message.status === 'failed') {
        return <AlertTriangle className="h-3 w-3" aria-label={title} />;
    }

    if (message.status === 'read') {
        return <CheckCheck className="wa-status-read h-3.5 w-3.5" aria-label={title} />;
    }

    if (message.status === 'delivered') {
        return <CheckCheck className="h-3.5 w-3.5" aria-label={title} />;
    }

    if (message.status === 'sent') {
        return <Check className="h-3.5 w-3.5" aria-label={title} />;
    }

    return null;
}

function isFormatBoundary(char?: string) {
    return !char || /[\s([{"'.,;:!?/\\-]/.test(char);
}

function renderWhatsappFormattedText(text: string, depth = 0): React.ReactNode[] {
    if (!text || depth > 3) {
        return [text];
    }

    const nodes: React.ReactNode[] = [];
    const formatPattern = /```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~/g;
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = formatPattern.exec(text)) !== null) {
        const token = match[0];
        const index = match.index;
        const before = text[index - 1];
        const after = text[index + token.length];
        const isCode = token.startsWith('```');

        if (index > cursor) {
            nodes.push(text.slice(cursor, index));
        }

        const content = isCode ? token.slice(3, -3) : token.slice(1, -1);
        const shouldFormat = content.length > 0
            && (isCode || content.trim() === content)
            && isFormatBoundary(before)
            && isFormatBoundary(after);

        if (!shouldFormat) {
            nodes.push(token);
        } else if (isCode) {
            nodes.push(
                <code
                    key={`${depth}-${index}-code`}
                    className="rounded bg-black/15 px-1 py-0.5 font-mono text-[0.92em]"
                >
                    {content}
                </code>,
            );
        } else if (token.startsWith('*')) {
            nodes.push(
                <strong key={`${depth}-${index}-bold`} className="font-semibold">
                    {renderWhatsappFormattedText(content, depth + 1)}
                </strong>,
            );
        } else if (token.startsWith('_')) {
            nodes.push(
                <em key={`${depth}-${index}-italic`} className="italic">
                    {renderWhatsappFormattedText(content, depth + 1)}
                </em>,
            );
        } else {
            nodes.push(
                <span key={`${depth}-${index}-strike`} className="line-through">
                    {renderWhatsappFormattedText(content, depth + 1)}
                </span>,
            );
        }

        cursor = index + token.length;
    }

    if (cursor < text.length) {
        nodes.push(text.slice(cursor));
    }

    return nodes.length ? nodes : [text];
}

function WhatsappFormattedText({
    text,
    className = '',
}: {
    text?: string | number | null;
    className?: string;
}) {
    const value = text === undefined || text === null ? '' : String(text);

    return (
        <span className={`whitespace-pre-wrap break-words ${className}`}>
            {renderWhatsappFormattedText(value)}
        </span>
    );
}

function getMessageMediaMetadata(message: WhatsappMessage) {
    const metadata = message.metadata || {};
    const presentation = metadata.presentation || {};
    const media = metadata.media || {};
    return {
        mediaId: media.mediaId || presentation.mediaId || metadata.mediaId || metadata.audio?.mediaId,
        mimeType: media.mimeType || presentation.mimeType || metadata.audio?.fileType,
        fileName: media.fileName || presentation.fileName || metadata.audio?.fileName,
        caption: media.caption || presentation.caption,
        size: media.size || metadata.audio?.size,
    };
}

function mediaCaption(message: WhatsappMessage, fallback?: string) {
    const media = getMessageMediaMetadata(message);
    const body = String(media.caption || message.body || '').trim();
    const genericLabels = new Set([
        'Imagem recebida',
        'Video recebido',
        'Audio recebido',
        'Audio enviado',
        'Mensagem',
    ]);

    if (!body || genericLabels.has(body)) {
        return fallback || '';
    }

    return body;
}

function MessageMediaPreview({
    message,
    kind,
}: {
    message: WhatsappMessage;
    kind: 'image' | 'video' | 'audio';
}) {
    const [mediaUrl, setMediaUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const objectUrlRef = useRef('');
    const media = getMessageMediaMetadata(message);
    const caption = mediaCaption(message);

    useEffect(() => {
        let canceled = false;

        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = '';
        }

        setMediaUrl('');
        setError('');

        if (!media.mediaId) {
            setError('Arquivo de mídia indisponível.');
            setLoading(false);
            return undefined;
        }

        setLoading(true);

        service
            .findMessageMedia(message.conversationId, message._id)
            .then(({ data }) => {
                const url = URL.createObjectURL(data);

                if (canceled) {
                    URL.revokeObjectURL(url);
                    return;
                }

                objectUrlRef.current = url;
                setMediaUrl(url);
            })
            .catch(() => {
                if (!canceled) {
                    setError('Não foi possível carregar a mídia.');
                }
            })
            .finally(() => {
                if (!canceled) {
                    setLoading(false);
                }
            });

        return () => {
            canceled = true;
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = '';
            }
        };
    }, [media.mediaId, message.conversationId, message._id]);

    const PlaceholderIcon = kind === 'image' ? ImageIcon : kind === 'video' ? Video : FileAudio2;
    const placeholder = (
        <div className="wa-media-placeholder">
            {loading ? (
                <>
                    <span className="loading loading-spinner loading-xs" />
                    <span>Carregando mídia</span>
                </>
            ) : (
                <>
                    <PlaceholderIcon className="h-4 w-4" aria-hidden />
                    <span>{error || 'Mídia indisponível'}</span>
                </>
            )}
        </div>
    );

    if (kind === 'image') {
        return (
            <div className="space-y-1.5">
                {mediaUrl ? (
                    <img
                        src={mediaUrl}
                        alt={caption || 'Imagem do WhatsApp'}
                        className="max-h-[420px] w-full rounded-[0.75rem] object-contain"
                    />
                ) : placeholder}
                {caption && (
                    <div className="wa-text">
                        <WhatsappFormattedText text={caption} />
                    </div>
                )}
            </div>
        );
    }

    if (kind === 'video') {
        return (
            <div className="space-y-1.5">
                {mediaUrl ? (
                    <video
                        src={mediaUrl}
                        className="max-h-[420px] w-full rounded-[0.75rem] bg-black"
                        controls
                        playsInline
                        preload="metadata"
                    />
                ) : placeholder}
                {caption && (
                    <div className="wa-text">
                        <WhatsappFormattedText text={caption} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="min-w-[13rem] max-w-full">
            {mediaUrl ? (
                <audio src={mediaUrl} className="h-9 w-full" controls preload="metadata" />
            ) : (
                <div className="wa-muted flex items-center gap-2">
                    <FileAudio2 className="h-4 w-4" aria-hidden />
                    {loading ? 'Carregando áudio' : error || 'Áudio indisponível'}
                </div>
            )}
            {!!media.size && <div className="wa-muted mt-1">{formatFileSize(Number(media.size))}</div>}
        </div>
    );
}

function MessageOptions({ items }: { items: any[] }) {
    return (
        <div className="wa-options">
            {items.map((item: any, index: number) => (
                <div key={`${item?.id || index}`} className="wa-option">
                    <WhatsappFormattedText text={item?.title || item?.id} />
                    {item?.description && (
                        <div className="wa-muted">
                            <WhatsappFormattedText text={item.description} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function MessageContent({ message }: { message: WhatsappMessage }) {
    const presentation = message.metadata?.presentation || {};
    const selectedOption = message.metadata?.selectedOption;
    const sections = Array.isArray(presentation.sections) ? presentation.sections : [];
    const buttons = Array.isArray(presentation.buttons) ? presentation.buttons : [];
    const body = presentation.bodyText || message.body || 'Mensagem';

    if (selectedOption) {
        const selectedOptionLabel = selectedOption.title
            || (message.body && message.body !== selectedOption.id ? message.body : '')
            || 'Resposta';

        return (
            <div>
                <div className="wa-muted">Opção selecionada</div>
                <div className="wa-text font-semibold">
                    <WhatsappFormattedText text={selectedOptionLabel} />
                </div>
            </div>
        );
    }

    if (presentation.kind === 'interactive') {
        return (
            <div className="space-y-2">
                <div className="wa-text">
                    <WhatsappFormattedText text={body} />
                </div>

                {presentation.footerText && <div className="wa-muted">{presentation.footerText}</div>}

                {sections.map((section: any, sectionIndex: number) => (
                    <div key={`${section?.title || 'section'}-${sectionIndex}`}>
                        {section?.title && <div className="wa-muted mb-1 font-semibold">{section.title}</div>}
                        <MessageOptions items={section?.rows || []} />
                    </div>
                ))}

                {buttons.length > 0 && <MessageOptions items={buttons} />}
            </div>
        );
    }

    if (presentation.kind === 'template') {
        const templateBody = resolveTemplateBody(message, presentation);
        return (
            <div className="space-y-2">
                <div className="wa-text">
                    <WhatsappFormattedText text={templateBody} />
                </div>

                {buttons.length > 0 && <MessageOptions items={buttons} />}
            </div>
        );
    }

    if (presentation.kind === 'image' || message.messageType === 'image') {
        return <MessageMediaPreview message={message} kind="image" />;
    }

    if (presentation.kind === 'video' || message.messageType === 'video') {
        return <MessageMediaPreview message={message} kind="video" />;
    }

    if (presentation.kind === 'audio' || message.messageType === 'audio') {
        return <MessageMediaPreview message={message} kind="audio" />;
    }

    return (
        <div className="wa-text">
            <WhatsappFormattedText text={message.body || 'Mensagem'} />
        </div>
    );
}

const SENDER_LABELS: Partial<Record<WhatsappMessage['senderType'], string>> = {
    BOT: 'Chatbot',
    ATTENDANT: 'Equipe',
    SYSTEM: 'Sistema',
};

function bubbleTone(message: WhatsappMessage) {
    if (message.direction !== 'OUTBOUND') return 'is-client';
    if (message.status === 'failed') return 'is-failed';
    return message.senderType === 'ATTENDANT' ? 'is-team' : 'is-bot';
}

// Mensagens seguidas do mesmo remetente formam um grupo, como no app Mensagens.
function isSameGroup(previous: WhatsappMessage | undefined, message: WhatsappMessage) {
    if (!previous) return false;
    if (previous.direction !== message.direction || previous.senderType !== message.senderType) return false;
    return new Date(message.createdAt).getTime() - new Date(previous.createdAt).getTime() < 5 * 60 * 1000;
}

function ThreadMessages({
    messages,
    loading,
    error,
    onRetry,
}: {
    messages: WhatsappMessage[];
    loading: boolean;
    error: boolean;
    onRetry: () => void;
}) {
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const pinnedRef = useRef(true);
    const previousCountRef = useRef(0);
    const [showJump, setShowJump] = useState(false);
    const [hasNew, setHasNew] = useState(false);

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior });
        pinnedRef.current = true;
        setShowJump(false);
        setHasNew(false);
    }, []);

    // Mensagem nova so desce a conversa se a pessoa ja estiver no fim ou se for dela mesma.
    useLayoutEffect(() => {
        const previousCount = previousCountRef.current;
        previousCountRef.current = messages.length;

        if (!messages.length || messages.length <= previousCount) return;

        if (previousCount === 0) {
            scrollToBottom();
        } else if (pinnedRef.current || messages[messages.length - 1].senderType === 'ATTENDANT') {
            scrollToBottom('smooth');
        } else {
            setHasNew(true);
        }
    }, [messages, scrollToBottom]);

    // Midias carregam depois e o teclado do celular reduz a area: mantem o fim visivel.
    useEffect(() => {
        const scroller = scrollerRef.current;
        const content = contentRef.current;
        if (!scroller || !content || typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(() => {
            if (pinnedRef.current) {
                scroller.scrollTop = scroller.scrollHeight;
            }
        });

        observer.observe(scroller);
        observer.observe(content);

        return () => observer.disconnect();
    }, []);

    const handleScroll = () => {
        const scroller = scrollerRef.current;
        if (!scroller) return;

        const pinned = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 80;
        pinnedRef.current = pinned;
        setShowJump(!pinned);
        if (pinned) setHasNew(false);
    };

    return (
        <div className="wa-messages-wrap">
            <div ref={scrollerRef} className="wa-messages" onScroll={handleScroll}>
                <div ref={contentRef} className="wa-messages-content" role="log" aria-live="polite">
                    {loading ? (
                        <div className="space-y-3" aria-busy="true">
                            <div className="sf-skeleton h-12 w-2/3" />
                            <div className="sf-skeleton ml-auto h-16 w-1/2" />
                            <div className="sf-skeleton h-10 w-1/2" />
                        </div>
                    ) : error ? (
                        <div className="sf-state">
                            <AlertTriangle className="mb-1 h-6 w-6 text-base-content/30" aria-hidden />
                            <h2>Não foi possível carregar as mensagens</h2>
                            <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>
                                Tentar novamente
                            </button>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="sf-state">
                            <MessageCircle className="mb-1 h-6 w-6 text-base-content/30" aria-hidden />
                            <h2>Nenhuma mensagem ainda</h2>
                            <p>Escreva abaixo para iniciar o atendimento.</p>
                        </div>
                    ) : (
                        messages.map((message, index) => {
                            const previous = messages[index - 1];
                            const outbound = message.direction === 'OUTBOUND';
                            const showDate =
                                index === 0 ||
                                getLocalDateKey(previous?.createdAt) !== getLocalDateKey(message.createdAt);
                            const grouped = !showDate && isSameGroup(previous, message);
                            const senderLabel = outbound && !grouped ? SENDER_LABELS[message.senderType] : '';

                            return (
                                <React.Fragment key={message._id}>
                                    {showDate && (
                                        <div className="wa-date">
                                            <span>{formatMessageDateLabel(message.createdAt)}</span>
                                        </div>
                                    )}
                                    <div className={`wa-message ${outbound ? 'is-outbound' : ''} ${grouped ? 'is-grouped' : ''}`}>
                                        {senderLabel && <div className="wa-sender">{senderLabel}</div>}
                                        <div className={`wa-bubble ${bubbleTone(message)}`}>
                                            <MessageContent message={message} />
                                            <div className="wa-meta" title={messageStatusTitle(message)}>
                                                <span>{formatTime(message.createdAt)}</span>
                                                <MessageStatus message={message} />
                                            </div>
                                        </div>
                                        {message.status === 'failed' && (
                                            <div className="wa-failed">
                                                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                                                {message.errorMessage || 'Não foi possível entregar esta mensagem.'}
                                            </div>
                                        )}
                                    </div>
                                </React.Fragment>
                            );
                        })
                    )}
                </div>
            </div>

            {showJump && (
                <button
                    type="button"
                    className={`wa-jump ${hasNew ? 'has-new' : ''}`}
                    onClick={() => scrollToBottom('smooth')}
                    aria-label={hasNew ? 'Ver novas mensagens' : 'Ir para o fim da conversa'}
                >
                    <ArrowDown className="h-4 w-4" aria-hidden />
                    {hasNew && <span>Novas mensagens</span>}
                </button>
            )}
        </div>
    );
}

function ClientOverviewContent({
    overview,
    loading,
    onOpenClient,
}: {
    overview: WhatsappClientOverview | null;
    loading: boolean;
    onOpenClient: (uid: string) => void;
}) {
    const client = overview?.client;

    if (loading) {
        return (
            <div className="space-y-3" aria-busy="true">
                <div className="sf-skeleton h-24" />
                <div className="sf-skeleton h-20" />
                <div className="sf-skeleton h-32" />
            </div>
        );
    }

    if (!overview || !client) {
        return (
            <div className="sf-state">
                <UserRound className="mb-1 h-6 w-6 text-base-content/30" aria-hidden />
                <h2>Não foi possível carregar o cliente</h2>
            </div>
        );
    }

    const title = client.displayName || 'Cliente';

    return (
        <div className="wa-overview">
            <div className="wa-overview-hero">
                <ContactAvatar name={title} large />
                <h3 className="wa-overview-name">{title}</h3>
                {!!client.phoneNumber && <div className="wa-muted">{formatPhone(client.phoneNumber)}</div>}
                {!!client.email && <div className="wa-muted break-all">{client.email}</div>}
                <div className="sf-tags justify-center">
                    {!client.linked && (
                        <span className="sf-tag" data-tone="gray">
                            Contato sem cadastro
                        </span>
                    )}
                    {client.selfSchedulingBlocked && (
                        <span className="sf-tag" data-tone="orange">
                            Autoagendamento bloqueado
                        </span>
                    )}
                </div>
                {client.linked && !!client.uid && (
                    <button type="button" className="sf-link" onClick={() => onOpenClient(client.uid as string)}>
                        Abrir cadastro
                    </button>
                )}
            </div>

            <div className="wa-metrics sf-group">
                <div>
                    <strong>{overview.metrics.totalAppointments}</strong>
                    <span>Agendamentos</span>
                </div>
                <div>
                    <strong>{overview.metrics.completedAppointments}</strong>
                    <span>Concluídos</span>
                </div>
                <div>
                    <strong>{overview.metrics.upcomingAppointments}</strong>
                    <span>Futuros</span>
                </div>
                <div>
                    <strong>{formatCurrency(overview.metrics.totalSpent)}</strong>
                    <span>Receita</span>
                </div>
            </div>

            <section>
                <h4 className="sf-group-title">
                    Últimos agendamentos
                    {!!overview.metrics.canceledAppointments && ` · ${overview.metrics.canceledAppointments} cancelados`}
                </h4>
                {overview.appointments.length === 0 ? (
                    <div className="sf-group wa-overview-empty">Nenhum agendamento encontrado.</div>
                ) : (
                    <ul className="sf-group">
                        {overview.appointments.map((appointment) => (
                            <li key={appointment._id} className="wa-overview-row">
                                <div className="min-w-0 flex-1">
                                    <div className="wa-overview-row-title">
                                        {appointment.serviceName || 'Serviço'}
                                        {appointment.extraServices?.length ? ` +${appointment.extraServices.length}` : ''}
                                    </div>
                                    <div className="wa-muted">
                                        {formatDateTime(appointment.startAt)}
                                        {appointment.attendantName ? ` · ${appointment.attendantName}` : ''}
                                    </div>
                                    {appointment.totalPrice !== undefined && (
                                        <div className="wa-muted">
                                            {formatCurrency(appointment.totalPrice)}
                                            {appointment.assinaturaId ? ' · Assinatura' : ''}
                                        </div>
                                    )}
                                </div>
                                <span className="sf-tag" data-tone={appointmentStatusTone(appointment.status)}>
                                    {appointmentStatusLabel(appointment.status)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section>
                <h4 className="sf-group-title">
                    Assinaturas
                    {!!overview.metrics.activeSubscriptions && ` · ${overview.metrics.activeSubscriptions} ativas`}
                </h4>
                {overview.subscriptions.length === 0 ? (
                    <div className="sf-group wa-overview-empty">Nenhuma assinatura encontrada.</div>
                ) : (
                    <ul className="sf-group">
                        {overview.subscriptions.map((subscription) => (
                            <li key={subscription._id} className="wa-overview-row">
                                <div className="min-w-0 flex-1">
                                    <div className="wa-overview-row-title">{subscription.planName || 'Assinatura'}</div>
                                    <div className="wa-muted">
                                        {formatCurrency(subscription.price)}
                                        {subscription.nextBillingAt
                                            ? ` · Próxima ${formatDate(subscription.nextBillingAt)}`
                                            : ` · Início ${formatDate(subscription.startAt) || '—'}`}
                                    </div>
                                </div>
                                <span className="sf-tag" data-tone={Number(subscription.status) === 1 ? 'green' : 'gray'}>
                                    {subscriptionStatusLabel(subscription.status)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

export default function WhatsappInbox() {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { id: selectedId, setParams, clear } = useParamsContext();
    const navigate = useNavigate();
    const moduleSheet = useModuleSheet();
    // Abaixo disso a conversa ocupa a tela inteira; a partir de 1440px, os dados do cliente ficam em painel lateral.
    const isNarrow = useMediaQuery({ maxWidth: 767 });
    const isWide = useMediaQuery({ minWidth: 1440 });
    const [conversations, setConversations] = useState<WhatsappConversation[]>([]);
    const [messages, setMessages] = useState<WhatsappMessage[]>([]);
    const [messagesError, setMessagesError] = useState(false);
    const [search, setSearch] = useState('');
    const [onlyUnread, setOnlyUnread] = useState(false);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [conversationToDelete, setConversationToDelete] = useState<WhatsappConversation | null>(null);
    const [inboxTop, setInboxTop] = useState(0);
    const [listScrolled, setListScrolled] = useState(false);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState('');
    const [, setPauseTick] = useState(0);
    const draft = drafts[selectedId] || '';
    // O rascunho pertence a conversa: trocar de conversa nao pode levar o texto para outro cliente.
    const setDraft = useCallback((value: string) => {
        const conversationId = selectedIdRef.current;
        setDrafts((current) => ({ ...current, [conversationId]: value }));
    }, []);
    const setSelectedId = (conversationId: string) => {
        if (conversationId) {
            setParams({ id: conversationId });
        } else {
            clear();
        }
    };
    const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);
    const [recording, setRecording] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [recordingFormat, setRecordingFormat] = useState<WhatsappRecordingFormat | null>(() =>
        getSupportedRecordingFormat(),
    );
    const [manualTemplates, setManualTemplates] = useState<WhatsappManualTemplate[]>([]);
    const [selectedTemplateName, setSelectedTemplateName] = useState('');
    const [startModalOpen, setStartModalOpen] = useState(false);
    const [clientOverviewOpen, setClientOverviewOpen] = useState(false);
    const [clientOverview, setClientOverview] = useState<WhatsappClientOverview | null>(null);
    const [startMode, setStartMode] = useState<'existing' | 'new'>('existing');
    const [clientSearch, setClientSearch] = useState('');
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [newContactName, setNewContactName] = useState('');
    const [newContactPhone, setNewContactPhone] = useState('');
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [deletingConversationId, setDeletingConversationId] = useState('');
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [loadingClientOverview, setLoadingClientOverview] = useState(false);
    const [sending, setSending] = useState(false);
    const [startingConversation, setStartingConversation] = useState(false);
    const [resumingBot, setResumingBot] = useState(false);
    const inboxRef = useRef<HTMLDivElement | null>(null);
    const threadRef = useRef<HTMLElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const messagesRequestIdRef = useRef(0);
    const audioInputRef = useRef<HTMLInputElement | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordingStreamRef = useRef<MediaStream | null>(null);
    const recordingChunksRef = useRef<Blob[]>([]);
    const keepRecordingResultRef = useRef(false);
    const recordingStartedAtRef = useRef(0);
    const recordingFormatRef = useRef<WhatsappRecordingFormat | null>(null);
    const recordingRequestIdRef = useRef(0);
    const selectedIdRef = useRef('');
    const conversationsRequestIdRef = useRef(0);

    const selectedConversationRef = useRef<WhatsappConversation | null>(null);
    const selectedConversation = useMemo(() => {
        const found = conversations.find((conversation) => conversation._id === selectedId);
        if (found) {
            selectedConversationRef.current = found;
            return found;
        }

        // A busca pode tirar a conversa aberta da lista sem que ela deva fechar.
        return selectedConversationRef.current?._id === selectedId ? selectedConversationRef.current : null;
    }, [conversations, selectedId]);
    // Evita mostrar por um instante as mensagens da conversa anterior ao trocar de cliente.
    const threadMessages = useMemo(
        () => messages.filter((message) => message.conversationId === selectedId),
        [messages, selectedId],
    );
    const visibleConversations = onlyUnread
        ? conversations.filter((conversation) => !!conversation.unreadCount || conversation._id === selectedId)
        : conversations;
    const unreadConversations = conversations.filter((conversation) => !!conversation.unreadCount).length;
    const overviewAsPanel = clientOverviewOpen && isWide;
    const activeTemplateName = selectedTemplateName || manualTemplates[0]?.name || 'message_text';

    const stopRecordingStream = useCallback(() => {
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
    }, []);

    const finishRecording = useCallback(() => {
        const shouldKeepResult = keepRecordingResultRef.current;
        const chunks = recordingChunksRef.current;
        const format = recordingFormatRef.current || getSupportedRecordingFormat();

        recordingChunksRef.current = [];
        keepRecordingResultRef.current = false;
        mediaRecorderRef.current = null;
        stopRecordingStream();
        setRecording(false);
        setRecordingSeconds(0);

        if (!shouldKeepResult) {
            return;
        }

        const blob = new Blob(chunks, { type: format?.recorderMimeType || 'audio/ogg;codecs=opus' });

        if (!blob.size) {
            errorSnackbar('Gravação vazia. Tente novamente.');
            return;
        }

        if (blob.size > WHATSAPP_AUDIO_MAX_BYTES) {
            errorSnackbar('O áudio deve ter no máximo 16 MB.');
            return;
        }

        const fileMimeType = format?.fileMimeType || 'audio/ogg';
        const extension = format?.extension || 'ogg';
        const file = new File([blob], `audio-${Date.now()}.${extension}`, { type: fileMimeType });

        setDraft('');
        setSelectedAudioFile(file);
    }, [errorSnackbar, stopRecordingStream]);

    const abortRecording = useCallback(() => {
        recordingRequestIdRef.current += 1;
        keepRecordingResultRef.current = false;
        const recorder = mediaRecorderRef.current;

        if (recorder) {
            recorder.ondataavailable = null;
            recorder.onerror = null;
            recorder.onstop = null;

            if (recorder.state !== 'inactive') {
                try {
                    recorder.stop();
                } catch {
                    // Recorder can already be stopping; cleanup below still releases the stream.
                }
            }
        }

        recordingChunksRef.current = [];
        mediaRecorderRef.current = null;
        stopRecordingStream();
        setRecording(false);
        setRecordingSeconds(0);
    }, [stopRecordingStream]);

    const handleStartRecording = useCallback(async () => {
        if (recording) {
            return;
        }

        if (!selectedConversation?.customerServiceWindowOpen) {
            errorSnackbar('Áudio só pode ser enviado dentro da janela de 24 horas.');
            return;
        }

        const supportedFormat = recordingFormat || getSupportedRecordingFormat();

        if (!supportedFormat) {
            errorSnackbar('Este navegador não grava em formato aceito pelo WhatsApp. Anexe um MP3, M4A, AAC, AMR ou OGG Opus.');
            return;
        }

        const requestId = recordingRequestIdRef.current + 1;
        recordingRequestIdRef.current = requestId;

        try {
            recordingFormatRef.current = supportedFormat;
            setRecordingFormat(supportedFormat);
            setSelectedAudioFile(null);
            if (audioInputRef.current) {
                audioInputRef.current.value = '';
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            if (recordingRequestIdRef.current !== requestId) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }

            recordingStreamRef.current = stream;
            const recorder = new MediaRecorder(stream, {
                mimeType: supportedFormat.recorderMimeType,
            });

            recordingChunksRef.current = [];
            keepRecordingResultRef.current = true;
            mediaRecorderRef.current = recorder;
            recordingStartedAtRef.current = Date.now();

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordingChunksRef.current.push(event.data);
                }
            };

            recorder.onerror = () => {
                keepRecordingResultRef.current = false;
                errorSnackbar('Não foi possível concluir a gravação do áudio.');
                if (recorder.state !== 'inactive') {
                    recorder.stop();
                }
            };

            recorder.onstop = finishRecording;
            recorder.start();
            setDraft('');
            setRecordingSeconds(0);
            setRecording(true);
        } catch {
            if (recordingRequestIdRef.current !== requestId) {
                return;
            }

            keepRecordingResultRef.current = false;
            recordingChunksRef.current = [];
            mediaRecorderRef.current = null;
            stopRecordingStream();
            setRecording(false);
            setRecordingSeconds(0);
            errorSnackbar('Não foi possível acessar o microfone do navegador.');
        }
    }, [
        errorSnackbar,
        finishRecording,
        recording,
        recordingFormat,
        selectedConversation?.customerServiceWindowOpen,
        stopRecordingStream,
    ]);

    const handleStopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;

        if (!recorder || recorder.state === 'inactive') {
            return;
        }

        recorder.requestData();
        recorder.stop();
    }, []);

    useEffect(() => {
        const supportedFormat = getSupportedRecordingFormat();
        recordingFormatRef.current = supportedFormat;
        setRecordingFormat(supportedFormat);
    }, []);

    useEffect(() => {
        if (!recording) {
            return undefined;
        }

        const interval = window.setInterval(() => {
            const seconds = Math.max(
                0,
                Math.floor((Date.now() - recordingStartedAtRef.current) / 1000),
            );
            setRecordingSeconds(seconds);
        }, 250);

        return () => window.clearInterval(interval);
    }, [recording]);

    useEffect(() => () => abortRecording(), [abortRecording]);

    useEffect(() => {
        selectedIdRef.current = selectedId;
        abortRecording();
        setSelectedAudioFile(null);
        setClientOverviewOpen(false);
        setClientOverview(null);
        if (audioInputRef.current) {
            audioInputRef.current.value = '';
        }
    }, [abortRecording, selectedId]);

    useEffect(() => {
        void service
            .findManualTemplates()
            .then(({ data }) => {
                setManualTemplates(data || []);
                if (data?.[0]?.name) {
                    setSelectedTemplateName((current) => current || data[0].name);
                }
            })
            .catch(() => {
                setManualTemplates([]);
                setSelectedTemplateName('message_text');
            });
    }, []);

    const loadConversations = useCallback(async (value: string) => {
        const requestId = conversationsRequestIdRef.current + 1;
        conversationsRequestIdRef.current = requestId;

        try {
            setLoadingConversations(true);
            const { data } = await service.findConversations(value.trim());

            // Ignore stale responses when the initial load/search requests overlap.
            if (requestId !== conversationsRequestIdRef.current) {
                return;
            }

            setConversations(data);

        } catch (error) {
            errorSnackbar('Não foi possível carregar as conversas do WhatsApp.');
        } finally {
            setLoadingConversations(false);
        }
    }, [errorSnackbar]);

    const loadMessages = useCallback(async (conversationId: string) => {
        const requestId = messagesRequestIdRef.current + 1;
        messagesRequestIdRef.current = requestId;
        setMessages([]);
        setMessagesError(false);

        if (!conversationId) {
            setLoadingMessages(false);
            return;
        }

        try {
            setLoadingMessages(true);
            const { data } = await service.findMessages(conversationId);

            // Trocar de conversa durante a carga nao pode exibir mensagens de outro cliente.
            if (requestId !== messagesRequestIdRef.current) {
                return;
            }

            setMessages(data.messages || []);
            setConversations((current) => upsertConversation(current, data.conversation));
        } catch (error) {
            if (requestId === messagesRequestIdRef.current) {
                setMessagesError(true);
            }
        } finally {
            if (requestId === messagesRequestIdRef.current) {
                setLoadingMessages(false);
            }
        }
    }, []);

    const loadClients = useCallback(async (value: string) => {
        try {
            setLoadingClients(true);
            const { data } = await userService.getClients(value.trim() || undefined);
            setClients(data || []);
        } catch (error) {
            errorSnackbar('Não foi possível carregar os clientes.');
        } finally {
            setLoadingClients(false);
        }
    }, [errorSnackbar]);

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            void loadConversations(search);
        }, 350);

        return () => window.clearTimeout(timeout);
    }, [search, loadConversations]);

    useEffect(() => {
        if (!startModalOpen || startMode !== 'existing') {
            return undefined;
        }

        const timeout = window.setTimeout(() => {
            void loadClients(clientSearch);
        }, 350);

        return () => window.clearTimeout(timeout);
    }, [clientSearch, loadClients, startModalOpen, startMode]);

    useEffect(() => {
        void loadMessages(selectedId);
    }, [selectedId, loadMessages]);

    // A altura disponivel depende do header movel e de avisos acima da inbox, como o de periodo de teste.
    useLayoutEffect(() => {
        const inbox = inboxRef.current;
        if (!inbox) return undefined;

        const measure = () => setInboxTop(inbox.getBoundingClientRect().top);
        measure();
        window.addEventListener('resize', measure);

        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (inbox.parentElement) {
            observer?.observe(inbox.parentElement);
        }

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, []);

    // No celular, a conversa acompanha a area visivel para o campo nao ficar atras do teclado.
    useEffect(() => {
        const thread = threadRef.current;
        const viewport = window.visualViewport;
        if (!isNarrow || !selectedId || !thread || !viewport) return undefined;

        const update = () => {
            thread.style.setProperty('--wa-viewport-height', `${viewport.height}px`);
            thread.style.setProperty('--wa-viewport-top', `${viewport.offsetTop}px`);
        };

        update();
        viewport.addEventListener('resize', update);
        viewport.addEventListener('scroll', update);

        return () => {
            viewport.removeEventListener('resize', update);
            viewport.removeEventListener('scroll', update);
        };
    }, [isNarrow, selectedId]);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`;
    }, [draft, selectedId]);

    useEffect(() => {
        if (!selectedAudioFile) {
            setAudioPreviewUrl('');
            return undefined;
        }

        const url = URL.createObjectURL(selectedAudioFile);
        setAudioPreviewUrl(url);

        return () => URL.revokeObjectURL(url);
    }, [selectedAudioFile]);

    // Atualiza a tela quando a pausa do chatbot termina, sem depender de nova mensagem.
    const pausedUntil = selectedConversation?.botPausedUntil;
    useEffect(() => {
        const remaining = pausedUntil ? new Date(pausedUntil).getTime() - Date.now() : 0;
        if (!(remaining > 0)) return undefined;

        const timeout = window.setTimeout(() => setPauseTick((tick) => tick + 1), remaining + 500);
        return () => window.clearTimeout(timeout);
    }, [pausedUntil]);

    useEffect(() => {
        const manager = new WebSocketManager();

        manager.onChannel('whatsapp:conversation', (conversation?: WhatsappConversation) => {
            if (!conversation?._id) return;
            setConversations((current) => upsertConversation(current, conversation));
        });

        manager.onChannel('whatsapp:message', (message?: WhatsappMessage) => {
            if (!message?._id) return;
            if (message.conversationId !== selectedIdRef.current) return;

            setMessages((current) => {
                if (current.some((item) => item._id === message._id)) {
                    return current.map((item) =>
                        item._id === message._id ? { ...item, ...message } : item,
                    );
                }
                return [...current, message];
            });

            if (message.direction === 'INBOUND') {
                void service
                    .markAsRead(message.conversationId)
                    .then(({ data }) => {
                        setConversations((current) => upsertConversation(current, data));
                    })
                    .catch(() => undefined);
            }
        });

        return () => manager.disconnect();
    }, []);


    const handleDeleteConversation = async (conversation: WhatsappConversation) => {
        try {
            setDeletingConversationId(conversation._id);
            await service.deleteConversation(conversation._id);
            setConversations((current) => current.filter((item) => item._id !== conversation._id));
            if (selectedId === conversation._id) {
                setSelectedId('');
            }
            setConversationToDelete(null);
            successSnackbar('Conversa removida da inbox.');
        } catch (error) {
            errorSnackbar('Não foi possível remover a conversa.');
        } finally {
            setDeletingConversationId('');
        }
    };

    const handleCloseStartModal = () => {
        setStartModalOpen(false);
        setNewContactName('');
        setNewContactPhone('');
        setClientSearch('');
    };

    const handleCloseClientOverview = () => {
        setClientOverviewOpen(false);
        setClientOverview(null);
    };

    const handleOpenClientOverview = async () => {
        if (!selectedConversation) {
            return;
        }

        try {
            setClientOverviewOpen(true);
            setLoadingClientOverview(true);
            const { data } = await service.findClientOverview(selectedConversation._id);
            setClientOverview(data);
            if (data?.conversation) {
                setConversations((current) => upsertConversation(current, data.conversation));
            }
        } catch (error) {
            errorSnackbar('Não foi possível carregar os dados do cliente.');
        } finally {
            setLoadingClientOverview(false);
        }
    };

    const handleOpenClient = (uid: string) => {
        navigate(`/clientes?id=${uid}`);
    };

    const startConversation = async (payload: WhatsappStartConversationPayload) => {
        try {
            setStartingConversation(true);
            const { data } = await service.startConversation(payload);
            setConversations((current) => upsertConversation(current, data));
            setSelectedId(data._id);
            handleCloseStartModal();
        } catch (error: any) {
            const message =
                error?.response?.data?.message ||
                'Não foi possível iniciar a conversa.';
            errorSnackbar(message);
        } finally {
            setStartingConversation(false);
        }
    };

    const handleStartNewContact = (event: FormEvent) => {
        event.preventDefault();

        if (!newContactName.trim() || !newContactPhone.trim()) {
            errorSnackbar('Informe nome e WhatsApp para criar o contato.');
            return;
        }

        void startConversation({
            name: newContactName.trim(),
            phoneNumber: newContactPhone.trim(),
        });
    };

    const handleAudioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (!selectedConversation?.customerServiceWindowOpen) {
            errorSnackbar('Áudio só pode ser enviado dentro da janela de 24 horas.');
            event.target.value = '';
            return;
        }

        if (file.size > WHATSAPP_AUDIO_MAX_BYTES) {
            errorSnackbar('O áudio deve ter no máximo 16 MB.');
            event.target.value = '';
            return;
        }

        if (!isSupportedAudioFile(file)) {
            errorSnackbar('Formato inválido. Use AAC, M4A/MP4, MP3, AMR ou OGG Opus.');
            event.target.value = '';
            return;
        }

        setDraft('');
        setSelectedAudioFile(file);
    };

    const clearSelectedAudio = () => {
        setSelectedAudioFile(null);
        if (audioInputRef.current) {
            audioInputRef.current.value = '';
        }
    };

    // A resposta pode chegar depois de trocar de conversa: so entra na conversa a que pertence.
    const appendMessage = (message?: WhatsappMessage) => {
        if (!message || message.conversationId !== selectedIdRef.current) {
            return;
        }

        setMessages((current) => {
            if (current.some((item) => item._id === message._id)) {
                return current.map((item) => (item._id === message._id ? { ...item, ...message } : item));
            }
            return [...current, message];
        });
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        if (!selectedConversation || (!draft.trim() && !selectedAudioFile)) {
            return;
        }

        const conversationId = selectedConversation._id;

        try {
            setSending(true);

            if (selectedAudioFile) {
                const fileContent = await fileToDataUrl(selectedAudioFile);
                const { data } = await service.sendAudio(conversationId, {
                    fileContent,
                    fileName: selectedAudioFile.name,
                    fileType: audioFileTypeForPayload(selectedAudioFile),
                    size: selectedAudioFile.size,
                });

                clearSelectedAudio();
                setConversations((current) => upsertConversation(current, data.conversation));
                appendMessage(data.message);
                return;
            }

            const { data } = await service.sendMessage(
                conversationId,
                draft.trim(),
                selectedConversation.customerServiceWindowOpen ? undefined : activeTemplateName,
            );
            setDrafts((current) => ({ ...current, [conversationId]: '' }));
            setConversations((current) => upsertConversation(current, data.conversation));
            appendMessage(data.message);
        } catch (error: any) {
            const message =
                error?.response?.data?.message ||
                'Não foi possível enviar pelo WhatsApp.';
            errorSnackbar(message);
        } finally {
            setSending(false);
        }
    };

    // No computador, Enter envia e Shift+Enter quebra a linha; no toque, Enter sempre quebra a linha.
    const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (
            event.key === 'Enter'
            && !event.shiftKey
            && !event.nativeEvent.isComposing
            && !window.matchMedia('(pointer: coarse)').matches
        ) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
        }
    };

    const handleResumeBot = async () => {
        if (!selectedConversation) return;

        try {
            setResumingBot(true);
            const { data } = await service.resumeBot(selectedConversation._id);
            setConversations((current) => upsertConversation(current, data));
            successSnackbar('Chatbot retomado para esta conversa.');
        } catch (error) {
            errorSnackbar('Não foi possível retomar o chatbot.');
        } finally {
            setResumingBot(false);
        }
    };

    const threadOpen = !isNarrow || !!selectedId;
    const paused = isPaused(selectedConversation);
    const windowOpen = !!selectedConversation?.customerServiceWindowOpen;
    const showComposerAction = !!draft.trim() || !!selectedAudioFile || !windowOpen || !recordingFormat;

    return (
        <>
        <div
            ref={inboxRef}
            className={`wa-inbox ${isNarrow ? 'is-narrow' : ''} ${overviewAsPanel ? 'has-panel' : ''}`}
            style={{ height: `calc(100dvh - ${inboxTop}px - 0.5rem)` }}
        >
            <aside className={`wa-list ${isNarrow && selectedId ? 'hidden' : ''}`}>
                <div className="wa-rows" onScroll={(event) => setListScrolled(event.currentTarget.scrollTop > 0)}>
                    <div className={`wa-list-head ${listScrolled ? 'is-scrolled' : ''}`}>
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-1">
                                {moduleSheet && (
                                    <button type="button" className="sf-back-button shrink-0" onClick={() => navigate('/')} aria-label="Voltar">
                                        <ChevronLeftIcon aria-hidden="true" />
                                    </button>
                                )}
                                <h1 className={`wa-list-title ${listScrolled ? 'is-condensed' : ''}`}>WhatsApp</h1>
                            </div>
                            <button
                                type="button"
                                className="wa-icon-button is-accent"
                                onClick={() => setStartModalOpen(true)}
                                aria-label="Nova conversa"
                                title="Nova conversa"
                            >
                                <MessageSquarePlus className="h-5 w-5" aria-hidden />
                            </button>
                        </div>
                    </div>
                    <div className="wa-list-filters">
                        <label className="sf-search">
                            <Search aria-hidden />
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Buscar conversa"
                                aria-label="Buscar conversa"
                            />
                        </label>
                        <div className="sf-segmented" role="group" aria-label="Filtrar conversas">
                            <button type="button" aria-pressed={!onlyUnread} onClick={() => setOnlyUnread(false)}>
                                Todas
                            </button>
                            <button type="button" aria-pressed={onlyUnread} onClick={() => setOnlyUnread(true)}>
                                Não lidas
                                {unreadConversations > 0 && <span className="sf-segmented-count">{unreadConversations}</span>}
                            </button>
                        </div>
                    </div>
                    {loadingConversations && conversations.length === 0 ? (
                        <div className="space-y-2 p-2" aria-busy="true">
                            {[0, 1, 2, 3, 4].map((item) => (
                                <div key={item} className="sf-skeleton h-14" />
                            ))}
                        </div>
                    ) : visibleConversations.length === 0 ? (
                        <div className="sf-state">
                            <MessageCircle className="mb-1 h-6 w-6 text-base-content/30" aria-hidden />
                            <h2>
                                {onlyUnread
                                    ? 'Nenhuma conversa não lida'
                                    : search
                                        ? 'Nenhuma conversa encontrada'
                                        : 'Nenhuma conversa ainda'}
                            </h2>
                            {!onlyUnread && !search && <p>As mensagens recebidas pelo WhatsApp aparecem aqui.</p>}
                        </div>
                    ) : (
                        <ul>
                            {visibleConversations.map((conversation) => {
                                const title = conversationTitle(conversation);
                                const unread = !!conversation.unreadCount;

                                return (
                                    <li key={conversation._id}>
                                        <button
                                            type="button"
                                            className={`wa-row ${unread ? 'is-unread' : ''}`}
                                            aria-current={conversation._id === selectedId ? 'true' : undefined}
                                            onClick={() => setSelectedId(conversation._id)}
                                        >
                                            <ContactAvatar name={title} />
                                            <span className="min-w-0 flex-1">
                                                <span className="wa-row-line">
                                                    <span className="wa-row-title">{title}</span>
                                                    <span className="wa-row-time">
                                                        {formatConversationTimestamp(conversation.lastMessageAt)}
                                                    </span>
                                                </span>
                                                <span className="wa-row-line">
                                                    <span className="wa-row-preview">
                                                        {conversation.lastMessageDirection === 'OUTBOUND' ? 'Você: ' : ''}
                                                        {conversation.lastMessagePreview || formatPhone(conversation.clientPhoneNumber)}
                                                    </span>
                                                    {isPaused(conversation) && (
                                                        <PauseCircle className="h-3.5 w-3.5 shrink-0 text-warning" aria-label="Chatbot pausado" />
                                                    )}
                                                    {unread && <span className="sf-unread-badge">{conversation.unreadCount}</span>}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </aside>

            {threadOpen && (
                <section ref={threadRef} className={`wa-thread ${isNarrow ? 'is-fullscreen' : ''}`}>
                    {!selectedId ? (
                        <div className="sf-state my-auto">
                            <MessageCircle className="mb-1 h-8 w-8 text-base-content/25" aria-hidden />
                            <h2>Selecione uma conversa</h2>
                            <p>Acompanhe o chatbot e responda pelo número do estabelecimento.</p>
                        </div>
                    ) : !selectedConversation ? (
                        loadingMessages ? (
                            <div className="m-4 space-y-3" aria-busy="true">
                                <div className="sf-skeleton h-12" />
                                <div className="sf-skeleton h-24" />
                            </div>
                        ) : (
                            <div className="sf-state my-auto">
                                <UserRound className="mb-1 h-6 w-6 text-base-content/30" aria-hidden />
                                <h2>Conversa não encontrada</h2>
                                <p>Ela pode ter sido removida da inbox.</p>
                                <button type="button" className="sf-link" onClick={() => setSelectedId('')}>
                                    Voltar para conversas
                                </button>
                            </div>
                        )
                    ) : (
                        <>
                            <header className="wa-thread-head">
                                {isNarrow && (
                                    <button
                                        type="button"
                                        className="sf-back-button shrink-0"
                                        onClick={() => setSelectedId('')}
                                        aria-label="Voltar para conversas"
                                    >
                                        <ChevronLeft className="h-5 w-5" aria-hidden />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="wa-thread-contact"
                                    onClick={() => (overviewAsPanel ? handleCloseClientOverview() : handleOpenClientOverview())}
                                    aria-expanded={clientOverviewOpen}
                                    title="Ver dados do cliente"
                                >
                                    <ContactAvatar name={conversationTitle(selectedConversation)} />
                                    <span className="min-w-0">
                                        <span className="wa-row-title block">{conversationTitle(selectedConversation)}</span>
                                        <span className="wa-muted block truncate">
                                            {formatPhone(selectedConversation.clientPhoneNumber)}
                                            {!paused && ' · Chatbot ativo'}
                                        </span>
                                    </span>
                                </button>
                                <Dropdown
                                    ariaLabel={`Ações da conversa com ${conversationTitle(selectedConversation)}`}
                                    unstyled
                                    buttonClass="wa-icon-button"
                                    isDisabled={deletingConversationId === selectedConversation._id}
                                    actions={[
                                        { label: 'Ver dados do cliente', action: handleOpenClientOverview },
                                        { label: 'Remover conversa', action: async () => setConversationToDelete(selectedConversation) },
                                    ]}
                                    label={<MoreHorizontal className="h-5 w-5" aria-hidden />}
                                />
                            </header>

                            {paused && (
                                <div className="wa-strip">
                                    <PauseCircle className="h-4 w-4 shrink-0 text-warning" aria-hidden />
                                    <span className="min-w-0 flex-1">
                                        Chatbot pausado até {formatTime(selectedConversation.botPausedUntil)} para o atendimento da equipe.
                                    </span>
                                    <button type="button" className="sf-link shrink-0" onClick={handleResumeBot} disabled={resumingBot}>
                                        Retomar
                                    </button>
                                </div>
                            )}

                            <ThreadMessages
                                key={selectedConversation._id}
                                messages={threadMessages}
                                loading={loadingMessages}
                                error={messagesError}
                                onRetry={() => void loadMessages(selectedConversation._id)}
                            />

                            <form onSubmit={handleSubmit} className="wa-composer">
                                {!windowOpen && (
                                    <div className="wa-template">
                                        <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        <span className="min-w-0 flex-1">
                                            Mais de 24 horas sem resposta do cliente: o envio usa um modelo aprovado.
                                        </span>
                                        {manualTemplates.length > 1 && (
                                            <select
                                                className="sf-select-field !h-8 max-w-[45%] !text-xs"
                                                value={activeTemplateName}
                                                onChange={(event) => setSelectedTemplateName(event.target.value)}
                                                disabled={sending}
                                                aria-label="Modelo de mensagem"
                                            >
                                                {manualTemplates.map((template) => (
                                                    <option key={template.name} value={template.name}>
                                                        {template.label || template.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                )}

                                {selectedAudioFile && (
                                    <div className="wa-audio-preview">
                                        <div className="flex items-center gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="wa-row-title text-sm">{selectedAudioFile.name}</div>
                                                <div className="wa-muted">{formatFileSize(selectedAudioFile.size)}</div>
                                            </div>
                                            <button
                                                type="button"
                                                className="wa-icon-button"
                                                onClick={clearSelectedAudio}
                                                disabled={sending}
                                                aria-label="Descartar áudio"
                                            >
                                                <X className="h-4 w-4" aria-hidden />
                                            </button>
                                        </div>
                                        {!!audioPreviewUrl && <audio src={audioPreviewUrl} className="mt-1 h-9 w-full" controls />}
                                    </div>
                                )}

                                <div className="wa-composer-row">
                                    <input
                                        ref={audioInputRef}
                                        type="file"
                                        className="hidden"
                                        accept={WHATSAPP_AUDIO_ACCEPT}
                                        onChange={handleAudioChange}
                                    />

                                    {recording ? (
                                        <>
                                            <button
                                                type="button"
                                                className="wa-icon-button"
                                                onClick={abortRecording}
                                                aria-label="Cancelar gravação"
                                            >
                                                <X className="h-5 w-5" aria-hidden />
                                            </button>
                                            <div className="wa-recording" role="status">
                                                <span className="wa-recording-dot" aria-hidden />
                                                Gravando {formatDuration(recordingSeconds)}
                                            </div>
                                            <button
                                                type="button"
                                                className="wa-send is-stop"
                                                onClick={handleStopRecording}
                                                aria-label="Parar gravação"
                                            >
                                                <Square className="h-3.5 w-3.5 fill-current" aria-hidden />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {windowOpen && !selectedAudioFile && (
                                                <button
                                                    type="button"
                                                    className="wa-icon-button"
                                                    onClick={() => audioInputRef.current?.click()}
                                                    disabled={sending}
                                                    aria-label="Anexar áudio"
                                                    title="Anexar áudio (AAC, M4A, MP3, AMR ou OGG, até 16 MB)"
                                                >
                                                    <Paperclip className="h-5 w-5" aria-hidden />
                                                </button>
                                            )}

                                            {!selectedAudioFile && (
                                                <textarea
                                                    ref={textareaRef}
                                                    className="wa-field"
                                                    value={draft}
                                                    maxLength={MAX_MESSAGE_LENGTH}
                                                    onChange={(event) => setDraft(event.target.value)}
                                                    onKeyDown={handleComposerKeyDown}
                                                    placeholder={windowOpen ? 'Mensagem' : 'Mensagem pelo modelo aprovado'}
                                                    aria-label="Mensagem"
                                                    rows={1}
                                                    disabled={sending}
                                                />
                                            )}

                                            {selectedAudioFile && <span className="flex-1" />}

                                            {showComposerAction ? (
                                                <button
                                                    type="submit"
                                                    className="wa-send"
                                                    disabled={sending || (!draft.trim() && !selectedAudioFile)}
                                                    aria-label="Enviar"
                                                >
                                                    {sending ? (
                                                        <span className="loading loading-spinner loading-xs" />
                                                    ) : (
                                                        <ArrowUp className="h-5 w-5" aria-hidden />
                                                    )}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="wa-icon-button"
                                                    onClick={handleStartRecording}
                                                    disabled={sending}
                                                    aria-label="Gravar áudio"
                                                >
                                                    <Mic className="h-5 w-5" aria-hidden />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>

                                {(!paused && !recording) || draft.length >= MAX_MESSAGE_LENGTH - 100 ? (
                                    <div className="wa-composer-note">
                                        <span>{!paused && !recording ? 'Ao responder, o chatbot pausa por 30 minutos.' : ''}</span>
                                        {draft.length >= MAX_MESSAGE_LENGTH - 100 && (
                                            <span className="tabular-nums">{draft.length}/{MAX_MESSAGE_LENGTH}</span>
                                        )}
                                    </div>
                                ) : null}
                            </form>
                        </>
                    )}
                </section>
            )}

            {overviewAsPanel && (
                <aside className="wa-panel" aria-label="Dados do cliente">
                    <div className="flex justify-end">
                        <button type="button" className="wa-icon-button" onClick={handleCloseClientOverview} aria-label="Fechar dados do cliente">
                            <X className="h-4 w-4" aria-hidden />
                        </button>
                    </div>
                    <ClientOverviewContent
                        overview={clientOverview}
                        loading={loadingClientOverview}
                        onOpenClient={handleOpenClient}
                    />
                </aside>
            )}
        </div>

        {clientOverviewOpen && !overviewAsPanel && (
            <Modal title="Cliente" onClose={handleCloseClientOverview} desktopClassName="sm:!max-w-xl">
                <ClientOverviewContent
                    overview={clientOverview}
                    loading={loadingClientOverview}
                    onOpenClient={handleOpenClient}
                />
            </Modal>
        )}

        {conversationToDelete && (
            <Modal
                title="Remover conversa?"
                onClose={() => setConversationToDelete(null)}
                desktopClassName="sm:!max-w-md"
            >
                <p className="text-sm text-base-content/70">
                    A conversa com {conversationTitle(conversationToDelete)} sai da lista, mas o histórico é mantido. Se o
                    cliente enviar uma nova mensagem, ela volta a aparecer.
                </p>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setConversationToDelete(null)}
                        disabled={!!deletingConversationId}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-error"
                        onClick={() => void handleDeleteConversation(conversationToDelete)}
                        disabled={!!deletingConversationId}
                    >
                        {!!deletingConversationId && <span className="loading loading-spinner loading-sm"></span>}
                        Remover
                    </button>
                </div>
            </Modal>
        )}

        {startModalOpen && (
            <Modal title="Nova conversa" onClose={handleCloseStartModal} desktopClassName="sm:!max-w-lg">
                <div className="estab-settings estab-settings--sheet space-y-4">
                    <div className="sf-segmented" role="group" aria-label="Destinatário">
                        <button
                            type="button"
                            aria-pressed={startMode === 'existing'}
                            onClick={() => setStartMode('existing')}
                            disabled={startingConversation}
                        >
                            Cliente
                        </button>
                        <button
                            type="button"
                            aria-pressed={startMode === 'new'}
                            onClick={() => setStartMode('new')}
                            disabled={startingConversation}
                        >
                            Novo contato
                        </button>
                    </div>

                    {startMode === 'existing' ? (
                        <>
                            <label className="sf-search">
                                <Search aria-hidden />
                                <input
                                    type="search"
                                    value={clientSearch}
                                    onChange={(event) => setClientSearch(event.target.value)}
                                    placeholder="Buscar cliente"
                                    aria-label="Buscar cliente"
                                    disabled={startingConversation}
                                />
                            </label>

                            {loadingClients && clients.length === 0 ? (
                                <div className="space-y-2" aria-busy="true">
                                    {[0, 1, 2].map((item) => (
                                        <div key={item} className="sf-skeleton h-14" />
                                    ))}
                                </div>
                            ) : clients.length === 0 ? (
                                <div className="sf-state">
                                    <UserRound className="mb-1 h-6 w-6 text-base-content/30" aria-hidden />
                                    <h2>Nenhum cliente encontrado</h2>
                                </div>
                            ) : (
                                <ul className="wa-client-list">
                                    {clients.slice(0, 50).map((client) => {
                                        const title = client.displayName || client.email || client.phoneNumber || 'Cliente';

                                        return (
                                            <li key={client.uid}>
                                                <button
                                                    type="button"
                                                    className="wa-row"
                                                    onClick={() => void startConversation({ clientId: client.uid })}
                                                    disabled={startingConversation || !client.phoneNumber}
                                                >
                                                    <ContactAvatar name={title} />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="wa-row-title block">{title}</span>
                                                        <span className="wa-row-preview block">
                                                            {client.phoneNumber ? formatPhone(client.phoneNumber) : 'Sem WhatsApp cadastrado'}
                                                        </span>
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </>
                    ) : (
                        <form onSubmit={handleStartNewContact} className="space-y-4">
                            <SettingsSection footer="O contato é adicionado aos clientes do estabelecimento.">
                                <SettingsRow label="Nome" htmlFor="wa-contact-name">
                                    <input
                                        id="wa-contact-name"
                                        className="input estab-field"
                                        value={newContactName}
                                        onChange={(event) => setNewContactName(event.target.value)}
                                        placeholder="Nome do contato"
                                        disabled={startingConversation}
                                        required
                                    />
                                </SettingsRow>
                                <SettingsRow label="WhatsApp" htmlFor="phone">
                                    <PhoneNumber
                                        value={newContactPhone}
                                        setValue={setNewContactPhone}
                                        label="WhatsApp"
                                        hideLabel
                                        placeholder="+55 (00) 0 0000-0000"
                                        inputClassName="input estab-field"
                                    />
                                </SettingsRow>
                            </SettingsSection>
                            <div className="estab-savebar">
                                <button
                                    type="submit"
                                    className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]"
                                    disabled={startingConversation}
                                >
                                    {startingConversation && <span className="loading loading-spinner loading-sm"></span>}
                                    Iniciar conversa
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </Modal>
        )}
        </>
    );
}
